import axios from 'axios';
import type {
  MLBGame,
  BetRecommendation,
  Bet,
  BankrollSettings,
  ExposureReport,
  BankrollSnapshot,
  PerformanceMetrics,
  CalibrationMetrics,
  Alert,
  LiveGameData,
  GameOdds,
  DataQualityReport,
  TimestampedOddsSnapshot,
  LineMovementSignal,
  CalibrationStatus,
  CandidateWeightComparison,
  WatchlistItem,
  PlatformTelemetry,
  WNBAGameContext,
  WNBAPlayerProjection,
  WNBAGame,
  WNBAAgentPerformanceMetric,
  WNBAModelStatus,
  WNBAInjuryReport,
  WNBATeamProfile,
  WNBAPlayerProfile,
  NBAGameContext,
  NBAPlayerProjection,
  NBAGame,
  NBAAgentPerformanceMetric,
  NBAModelStatus,
  NBAInjuryReport,
  NBATeamProfile,
  NBAPlayerProfile,
  Parlay,
  ParlayRecommendation,
} from '../types';

export interface GameContext {
  game: MLBGame;
  odds?: GameOdds;
  weather?: {
    temperature: number;
    windSpeed: number;
    windDirection: string;
    condition: string;
    precipitationProbability?: number;
  };
  awayStandings?: { winPct: number; wins: number; losses: number };
  homeStandings?: { winPct: number; wins: number; losses: number };
  dataQuality: DataQualityReport;
  fetchedAt: string;
}

const api = axios.create({
  baseURL: '/api',
  timeout: 15_000,
});

// Attach Supabase JWT as a Bearer token on every outgoing request.
// Import is dynamic to avoid a circular dep at module init time.
// When auth is not configured (bootstrap/dev/CI mode) this is a no-op.
api.interceptors.request.use(async (config) => {
  try {
    const { supabase, isAuthConfigured } = await import('../lib/supabase');
    if (!isAuthConfigured) return config;

    const { useAuthStore } = await import('../store/useAuthStore');
    let accessToken = useAuthStore.getState().session?.access_token;

    if (!accessToken) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      accessToken = session?.access_token;
    }

    if (accessToken) {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
  } catch {
    /* no session — fall through to bootstrap mode */
  }
  return config;
});

// Unwrap { data, meta, error } envelope from all responses.
// On error, reject with the server's error message so callers get a real Error.
api.interceptors.response.use((response) => {
  const envelope = response.data as {
    data: unknown;
    meta: unknown;
    error: { message: string } | null;
  } | null;
  if (envelope && typeof envelope === 'object' && 'error' in envelope && 'data' in envelope) {
    if (envelope.error) {
      return Promise.reject(new Error(envelope.error.message));
    }
    response.data = envelope.data;
  }
  return response;
});

// On 401, attempt one token refresh and retry. If the refresh fails, sign out.
// 403 is intentionally NOT handled here — the onboarding flow uses 403 to signal
// "no org yet" and ProtectedRoute's catch path routes to /onboarding.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status as number | undefined;

    if (status === 401) {
      try {
        const { supabase, isAuthConfigured } = await import('../lib/supabase');
        const { useAuthStore } = await import('../store/useAuthStore');
        if (isAuthConfigured) {
          const originalRequest = error.config as
            | (typeof error.config & { _authRetry?: boolean })
            | undefined;
          const currentSession = useAuthStore.getState().session;

          if (!originalRequest?._authRetry) {
            const { data, error: refreshError } = await supabase.auth.refreshSession(
              currentSession?.refresh_token
                ? { refresh_token: currentSession.refresh_token }
                : undefined,
            );

            if (!refreshError && data.session?.access_token) {
              originalRequest._authRetry = true;
              originalRequest.headers = originalRequest.headers ?? {};
              originalRequest.headers['Authorization'] = `Bearer ${data.session.access_token}`;
              useAuthStore.getState().setSession(data.session);
              return api.request(originalRequest);
            }
          }

          const sessionExpiresAt = currentSession?.expires_at ?? 0;
          const sessionIsExpired = sessionExpiresAt > 0 && sessionExpiresAt <= Date.now() / 1000;
          if (!currentSession || sessionIsExpired) {
            await useAuthStore.getState().signOut();
          }
        }
      } catch {
        /* best-effort cleanup */
      }
    }

    return Promise.reject(error);
  },
);

// ── Games ──────────────────────────────────────

export const gamesApi = {
  getSlate: (date?: string) =>
    api.get<GameContext[]>('/games', { params: { date }, timeout: 60_000 }).then((r) => r.data),
  getGame: (gamePk: number, date?: string) =>
    api.get<GameContext>(`/games/${gamePk}`, { params: { date } }).then((r) => r.data),
  getLiveGame: (gamePk: number) =>
    api.get<LiveGameData>(`/games/${gamePk}/live`).then((r) => r.data),
};

// ── WNBA ───────────────────────────────────────

export const wnbaApi = {
  getSlate: (date?: string) =>
    api.get<WNBAGameContext[]>('/basketball/wnba/slate', { params: { date }, timeout: 60_000 }).then((r) => r.data),
  getGame: (eventPk: string) =>
    api.get<WNBAGameContext>(`/basketball/wnba/games/${eventPk}`).then((r) => r.data),
  getLiveGame: (eventPk: string) =>
    api.get<WNBAGameContext>(`/basketball/wnba/games/${eventPk}/live`).then((r) => r.data),
  getRecommendations: (date?: string) =>
    api.get<BetRecommendation[]>('/advisor/recommendations', { params: { leagueId: 'wnba', date } }).then((r) => r.data),
  getAuditCandidates: (date: string) =>
    api.get<BetRecommendation[]>('/advisor/recommendations', { params: { leagueId: 'wnba', date, includeAudit: true } }).then((r) => r.data),
  analyzeGame: (eventPk: string) =>
    api.post<BetRecommendation[]>(`/advisor/analyze/${eventPk}`, { leagueId: 'wnba' }).then((r) => r.data),
  getGameProps: (eventPk: string) =>
    api.get<WNBAPlayerProjection[]>(`/basketball/wnba/games/${eventPk}/props`).then((r) => r.data),
  getSlateProps: (date?: string) =>
    api.get<{ game: WNBAGame; props: WNBAPlayerProjection[] }[]>('/basketball/wnba/slate-props', { params: { date } }).then((r) => r.data),
  getModelStatus: () =>
    api.get<WNBAModelStatus>('/basketball/wnba/model/status').then((r) => r.data),
  getAgentPerformance: () =>
    api.get<WNBAAgentPerformanceMetric[]>('/basketball/wnba/agent-performance').then((r) => r.data),
  getTeam: (teamId: string) =>
    api.get<WNBATeamProfile>(`/basketball/wnba/teams/${teamId}`).then((r) => r.data),
  getPlayer: (playerId: string, teamId?: string) =>
    api.get<WNBAPlayerProfile>(`/basketball/wnba/players/${playerId}`, { params: teamId ? { teamId } : undefined }).then((r) => r.data),
  getInjuries: (date?: string) =>
    api.get<WNBAInjuryReport[]>('/basketball/wnba/injuries', { params: { date } }).then((r) => r.data),
};

// ── NBA ────────────────────────────────────────

export const nbaApi = {
  getSlate: (date?: string) =>
    api.get<NBAGameContext[]>('/basketball/nba/slate', { params: { date }, timeout: 60_000 }).then((r) => r.data),
  getGame: (eventPk: string) =>
    api.get<NBAGameContext>(`/basketball/nba/games/${eventPk}`).then((r) => r.data),
  getLiveGame: (eventPk: string) =>
    api.get<NBAGameContext>(`/basketball/nba/games/${eventPk}/live`).then((r) => r.data),
  getRecommendations: (date?: string) =>
    api.get<BetRecommendation[]>('/advisor/recommendations', { params: { leagueId: 'nba', date } }).then((r) => r.data),
  getAuditCandidates: (date: string) =>
    api.get<BetRecommendation[]>('/advisor/recommendations', { params: { leagueId: 'nba', date, includeAudit: true } }).then((r) => r.data),
  analyzeGame: (eventPk: string) =>
    api.post<BetRecommendation[]>(`/advisor/analyze/${eventPk}`, { leagueId: 'nba' }).then((r) => r.data),
  getGameProps: (eventPk: string) =>
    api.get<NBAPlayerProjection[]>(`/basketball/nba/games/${eventPk}/props`).then((r) => r.data),
  getSlateProps: (date?: string) =>
    api.get<{ game: NBAGame; props: NBAPlayerProjection[] }[]>('/basketball/nba/slate-props', { params: { date } }).then((r) => r.data),
  getModelStatus: () =>
    api.get<NBAModelStatus>('/basketball/nba/model/status').then((r) => r.data),
  getAgentPerformance: () =>
    api.get<NBAAgentPerformanceMetric[]>('/basketball/nba/agent-performance').then((r) => r.data),
  getTeam: (teamId: string) =>
    api.get<NBATeamProfile>(`/basketball/nba/teams/${teamId}`).then((r) => r.data),
  getPlayer: (playerId: string, teamId?: string) =>
    api.get<NBAPlayerProfile>(`/basketball/nba/players/${playerId}`, { params: teamId ? { teamId } : undefined }).then((r) => r.data),
  getInjuries: (date?: string) =>
    api.get<NBAInjuryReport[]>('/basketball/nba/injuries', { params: { date } }).then((r) => r.data),
};

// ── Advisor ────────────────────────────────────

export const advisorApi = {
  getRecommendations: (date?: string, refresh = false) =>
    api
      .get<
        BetRecommendation[]
      >('/advisor/recommendations', { params: { date, refresh }, timeout: refresh ? 60_000 : undefined })
      .then((r) => r.data),
  getAuditCandidates: (date: string, eventPk?: string, leagueId?: string) =>
    api
      .get<BetRecommendation[]>('/advisor/recommendations', {
        params: { date, ...(eventPk ? { eventPk } : {}), leagueId: leagueId ?? 'mlb', includeAudit: true },
      })
      .then((r) => r.data),
  analyzeGame: (gamePk: number, date?: string) =>
    api.post<BetRecommendation[]>(`/advisor/analyze/${gamePk}`, { date }).then((r) => r.data),
  getParlayRecommendations: (date?: string, opts?: { maxLegs?: number; count?: number }) =>
    api
      .get<ParlayRecommendation[]>('/advisor/parlays', { params: { date, ...opts } })
      .then((r) => r.data),
};

// ── Parlays ────────────────────────────────────

export const parlaysApi = {
  getAll: (params?: { status?: string; date?: string; limit?: number }) =>
    api.get<Parlay[]>('/parlays', { params }).then((r) => r.data),
  getById: (id: string) => api.get<Parlay>(`/parlays/${id}`).then((r) => r.data),
  place: (data: {
    legs: Parlay['legs'];
    stakeAmount?: number;
    stakeUnits?: number;
    notes?: string;
    leagueId?: string;
    gameDate?: string;
  }) => api.post<Parlay>('/parlays', data).then((r) => r.data),
  settle: (
    id: string,
    result: 'won' | 'lost' | 'push' | 'void',
    legResults?: { legId: string; result: 'won' | 'lost' | 'push' | 'void' }[],
  ) => api.post<Parlay>(`/parlays/${id}/settle`, { result, legResults }).then((r) => r.data),
  remove: (id: string) => api.delete(`/parlays/${id}`).then((r) => r.data),
};

// ── Bets ───────────────────────────────────────

export const betsApi = {
  getAll: (params?: { status?: string; date?: string; limit?: number }) =>
    api.get<Bet[]>('/bets', { params }).then((r) => r.data),
  getById: (id: string) => api.get<Bet>(`/bets/${id}`).then((r) => r.data),
  place: (data: {
    eventPk?: string;
    gamePk?: number;
    leagueId?: string;
    gameDate?: string;
    awayTeam?: string;
    homeTeam?: string;
    market: string;
    selection: string;
    odds: number;
    stakeAmount: number;
    stakeUnits?: number;
    modelProbability?: number;
    edge?: number;
    confidence?: number;
    recommendationId?: string;
    notes?: string;
    isMultiplier?: boolean;
    multiplierBonus?: number;
  }) => api.post<Bet>('/bets', data).then((r) => r.data),
  updateStatus: (
    id: string,
    status: string,
    extra?: { result?: string; pnl?: number; currentOdds?: number },
  ) => api.patch<Bet>(`/bets/${id}`, { status, ...extra }).then((r) => r.data),
  settle: (id: string, result: 'won' | 'lost' | 'push' | 'void', closingOdds?: number) =>
    api.post<Bet>(`/bets/${id}/settle`, { result, closingOdds }).then((r) => r.data),
  delete: (id: string) => api.delete(`/bets/${id}`).then((r) => r.data),
};

// ── Bankroll ───────────────────────────────────

export const bankrollApi = {
  get: () =>
    api
      .get<{ settings: BankrollSettings; exposure: ExposureReport }>('/bankroll')
      .then((r) => r.data),
  updateSettings: (settings: Partial<BankrollSettings>) =>
    api.patch<BankrollSettings>('/bankroll/settings', settings).then((r) => r.data),
  reset: (amount: number) =>
    api.post<BankrollSettings>('/bankroll/reset', { amount }).then((r) => r.data),
  deposit: (amount: number) =>
    api.post<BankrollSettings>('/bankroll/deposit', { amount }).then((r) => r.data),
  withdraw: (amount: number) =>
    api.post<BankrollSettings>('/bankroll/withdraw', { amount }).then((r) => r.data),
  getExposure: () => api.get<ExposureReport>('/bankroll/exposure').then((r) => r.data),
  getHistory: () => api.get<BankrollSnapshot[]>('/bankroll/history').then((r) => r.data),
  getAnalytics: () => api.get<PerformanceMetrics>('/bankroll/analytics').then((r) => r.data),
  getCalibration: () => api.get<CalibrationMetrics>('/bankroll/calibration').then((r) => r.data),
};

// ── Alerts ─────────────────────────────────────

export const alertsApi = {
  getAll: () => api.get<Alert[]>('/alerts').then((r) => r.data),
  markRead: (id: number) => api.patch(`/alerts/${id}/read`).then((r) => r.data),
  markAllRead: () => api.patch('/alerts/read-all').then((r) => r.data),
};

// ── Watchlist ──────────────────────────────────

export const watchlistApi = {
  getAll: () => api.get<WatchlistItem[]>('/watchlist').then((r) => r.data),
  add: (gamePk: number, recommendationId?: string) =>
    api.post<WatchlistItem>('/watchlist', { gamePk, recommendationId }).then((r) => r.data),
  remove: (gamePk: number) => api.delete(`/watchlist/${gamePk}`).then((r) => r.data),
};

// ── Settings ────────────────────────────────────

export const settingsApi = {
  get: () => api.get<Record<string, string>>('/settings').then((r) => r.data),
  update: (settings: Record<string, string>) =>
    api.patch('/settings', settings).then((r) => r.data),
};

// ── Calibration ────────────────────────────────

export interface CalibrationRunMetrics {
  runAt: string;
  trainCount: number;
  holdOutCount: number;
  factorsCalibrated: string[];
  brierScoreCandidate: number;
  brierScoreNull: number;
  brierCiLower?: number;
  brierCiUpper?: number;
  shouldPromote: boolean;
  factorPValues?: {
    factor: string;
    n: number;
    slope: number;
    pValue: number;
    zScore: number;
    isSignificant: boolean;
  }[];
}

export const calibrationApi = {
  getStatus: () => api.get<CalibrationStatus>('/calibration/status').then((r) => r.data),
  run: () =>
    api
      .post<{
        success: boolean;
        output: string;
        candidateWritten: boolean;
      }>('/calibration/run', {}, { timeout: 120_000 })
      .then((r) => r.data),
  getCandidate: () =>
    api.get<CandidateWeightComparison>('/calibration/candidate').then((r) => r.data),
  promote: () => api.post<{ success: boolean }>('/calibration/promote').then((r) => r.data),
  discard: () => api.post<{ success: boolean }>('/calibration/discard').then((r) => r.data),
  getMetrics: () =>
    api.get<CalibrationRunMetrics | null>('/calibration/metrics').then((r) => r.data),
};

// ── Odds ───────────────────────────────────────

export interface ProviderHealth {
  name: string;
  totalFetches: number;
  successfulFetches: number;
  failedFetches: number;
  emptyResponses: number;
  parseFailures: number;
  totalLatencyMs: number;
  avgLatencyMs: number;
  successRate: number;
  emptyResponseRate: number;
  parseFailureRate: number;
  lastSuccessAt?: string;
  lastFetchAt?: string;
  lastError?: string;
  quotaUsed?: number;
  lastPayloadSize?: number;
  lastExtractedGameCount?: number;
  lastDataAt?: string;
}

export const oddsApi = {
  getLineMovement: (awayTeam: string, homeTeam: string) =>
    api
      .get<{
        history: TimestampedOddsSnapshot[];
        signal: LineMovementSignal;
      }>('/odds/line-movement', { params: { awayTeam, homeTeam } })
      .then((r) => r.data),
  getHealth: () =>
    api
      .get<{
        providers: ProviderHealth[];
        totalProviders: number;
        activeProviders: number;
        checkedAt: string;
      }>('/odds/health')
      .then((r) => r.data),
};

// ── Backtest ───────────────────────────────────

export interface BacktestRun {
  id: string;
  start_date: string;
  end_date: string;
  status: 'running' | 'complete' | 'failed';
  total_games: number;
  total_recs: number;
  config: { minStatus: string } | null;
  created_at: string;
  completed_at: string | null;
}

export interface BacktestSummary {
  overall: {
    total_recs: number;
    settled: number;
    wins: number;
    losses: number;
    pushes: number;
    total_staked: number;
    net_pnl: number;
    avg_edge: number | null;
    avg_confidence: number | null;
    winRate: number | null;
    roi: number | null;
  };
  byMarket: {
    market: string;
    recs: number;
    wins: number;
    losses: number;
    net_pnl: number;
    staked: number;
    roi: number | null;
    winRate: number | null;
  }[];
  byDecision: {
    decision_status: string;
    recs: number;
    wins: number;
    losses: number;
    net_pnl: number;
    staked: number;
    roi: number | null;
    winRate: number | null;
  }[];
  equityCurve: { date: string; cumPnl: number; dailyRecs: number }[];
}

export const backtestApi = {
  getRuns: () => api.get<BacktestRun[]>('/backtest/runs').then((r) => r.data),
  getRun: (id: string) => api.get<BacktestRun>(`/backtest/runs/${id}`).then((r) => r.data),
  getSummary: (id: string) =>
    api.get<BacktestSummary>(`/backtest/runs/${id}/summary`).then((r) => r.data),
  startRun: (startDate: string, endDate: string, minStatus?: string) =>
    api
      .post<{ message: string; pid: number; startDate: string; endDate: string }>('/backtest/run', {
        startDate,
        endDate,
        minStatus,
      })
      .then((r) => r.data),
};

// ── Members / Team Management ─────────────────

export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer';
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface OrgMember {
  userId: string;
  organizationId: string;
  role: OrgRole;
  joinedAt: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface OrgInvitation {
  id: string;
  organizationId: string;
  email: string;
  role: OrgRole;
  invitedBy: string;
  invitedByEmail: string | null;
  invitedByName: string | null;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  memberCount: number;
  pendingInviteCount: number;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
  subscriptionTier: string;
}

export const membersApi = {
  listMembers: () => api.get<OrgMember[]>('/members').then((r) => r.data),
  getSummary: () => api.get<OrgSummary>('/members/summary').then((r) => r.data),
  listInvitations: () => api.get<OrgInvitation[]>('/members/invitations').then((r) => r.data),
  invite: (email: string, role: OrgRole) =>
    api.post<OrgInvitation>('/members/invite', { email, role }).then((r) => r.data),
  updateRole: (userId: string, role: OrgRole) =>
    api
      .patch<{ userId: string; role: OrgRole }>(`/members/${userId}/role`, { role })
      .then((r) => r.data),
  removeMember: (userId: string) => api.delete(`/members/${userId}`).then((r) => r.data),
  transferOwnership: (targetUserId: string) =>
    api
      .post<{
        previousOwnerId: string;
        newOwnerId: string;
      }>('/members/transfer-ownership', { targetUserId })
      .then((r) => r.data),
  revokeInvitation: (invitationId: string) =>
    api.delete(`/members/invitations/${invitationId}`).then((r) => r.data),
  resendInvitation: (invitationId: string) =>
    api.post<OrgInvitation>(`/members/invitations/${invitationId}/resend`, {}).then((r) => r.data),
};

// ── Join Requests ──────────────────────────────

export const joinRequestsApi = {
  list: (status?: 'pending' | 'accepted' | 'denied') =>
    api.get<OrgJoinRequest[]>('/members/join-requests', status ? { params: { status } } : undefined).then((r) => r.data),
  accept: (id: string) =>
    api
      .post<{ userId: string; role: string }>(`/members/join-requests/${id}/accept`, {})
      .then((r) => r.data),
  deny: (id: string) => api.post(`/members/join-requests/${id}/deny`, {}).then((r) => r.data),
  requestToJoin: (slug: string, message?: string) =>
    api
      .post<{
        organizationId: string;
        organizationName: string;
        status: string;
      }>('/organizations/request-join', { slug, message })
      .then((r) => r.data),
};

// ── Invitations (public) ───────────────────────

export interface InvitationPreview {
  id: string;
  organizationId: string;
  organizationName: string | null;
  maskedEmail: string;
  role: OrgRole;
  inviterName: string | null;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
}

export interface AcceptInvitationResult {
  organizationId: string;
  organizationName: string | null;
  role: string;
}

export const invitationsApi = {
  preview: (token: string) =>
    api.get<InvitationPreview>(`/invitations/preview/${token}`).then((r) => r.data),
  accept: (token: string) =>
    api.post<AcceptInvitationResult>('/invitations/accept', { token }).then((r) => r.data),
};

// ── Billing ────────────────────────────────────

export interface BillingSubscription {
  id: string;
  organizationId: string;
  plan: string;
  status: string;
  trialStart: string | null;
  trialEnd: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  billingInterval: 'month' | 'year' | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillingEntitlements {
  plan: string;
  status: string;
  capabilities: {
    analytics: boolean;
    simulator: boolean;
    backtesting: boolean;
  };
  isTrialing: boolean;
  trialEndsAt?: string;
  renewsAt?: string;
  cancelAtPeriodEnd: boolean;
}

export interface OrgJoinRequest {
  id: string;
  organizationId: string;
  organizationName: string;
  userId: string;
  userEmail: string;
  userDisplayName: string | null;
  message: string | null;
  status: 'pending' | 'accepted' | 'denied';
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface BillingInvoice {
  id: string;
  stripeInvoiceId: string | null;
  amountPaid: number;
  amountDue: number;
  currency: string;
  status: string;
  invoiceUrl: string | null;
  invoicePdf: string | null;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  createdAt: string;
}

export interface BillingUsage {
  period: string;
  byType: Record<string, number>;
  total: number;
}

export const billingApi = {
  getEntitlements: () => api.get<BillingEntitlements>('/billing/entitlements').then((r) => r.data),
  getSubscription: () => api.get<BillingSubscription>('/billing/subscription').then((r) => r.data),
  getInvoices: () => api.get<BillingInvoice[]>('/billing/invoices').then((r) => r.data),
  getUsage: () => api.get<BillingUsage>('/billing/usage').then((r) => r.data),
  createCheckout: (plan: string, interval: 'month' | 'year' = 'month') =>
    api.post<{ url: string }>('/billing/checkout', { plan, interval }).then((r) => r.data),
  createPortalSession: () => api.post<{ url: string }>('/billing/portal', {}).then((r) => r.data),
  cancelSubscription: () =>
    api.post<{ cancelAtPeriodEnd: boolean }>('/billing/cancel', {}).then((r) => r.data),
  reactivateSubscription: () =>
    api.post<{ cancelAtPeriodEnd: boolean }>('/billing/reactivate', {}).then((r) => r.data),
};

// ── Profile ────────────────────────────────────

export interface UserProfile {
  id: string;
  email?: string;
  display_name?: string | null;
  full_name?: string | null;
  bio?: string | null;
  job_title?: string | null;
  timezone?: string | null;
  locale?: string | null;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface UserAuditEntry {
  id: number;
  action: string;
  metadata?: Record<string, unknown> | null;
  ip_address?: string | null;
  created_at: string;
}

export const profileApi = {
  get: () => api.get<UserProfile>('/profile').then((r) => r.data),
  update: (
    updates: Partial<
      Pick<UserProfile, 'display_name' | 'full_name' | 'bio' | 'job_title' | 'timezone' | 'locale'>
    >,
  ) => api.patch<UserProfile>('/profile', updates).then((r) => r.data),
  uploadAvatar: (fileName: string, contentType: string, base64: string) =>
    api
      .post<{ avatarUrl: string }>('/profile/avatar', { fileName, contentType, base64 })
      .then((r) => r.data),
  removeAvatar: () => api.delete('/profile/avatar').then((r) => r.data),
  getAuditLog: (limit?: number) =>
    api.get<UserAuditEntry[]>('/profile/audit-log', { params: { limit } }).then((r) => r.data),
};

// ── Sessions ───────────────────────────────────

export interface UserSession {
  id: string;
  isCurrent: boolean;
  userAgent?: string;
  lastActive?: string;
  createdAt?: string;
}

export const sessionsApi = {
  list: () => api.get<UserSession[]>('/sessions').then((r) => r.data),
  revoke: (sessionId: string) => api.delete(`/sessions/${sessionId}`).then((r) => r.data),
  revokeOthers: () => api.delete('/sessions/other').then((r) => r.data),
};

// ── User Preferences ───────────────────────────

export interface UserPreferences {
  theme?: string;
  odds_format?: string;
  preferred_sport?: string | null;
  default_dashboard?: string | null;
  updated_at?: string;
}

export const preferencesApi = {
  get: () => api.get<UserPreferences>('/preferences').then((r) => r.data),
  update: (prefs: Partial<UserPreferences>) =>
    api.patch<UserPreferences>('/preferences', prefs).then((r) => r.data),
};

// ── Notification Preferences ───────────────────

export interface NotificationPreferences {
  betting_alerts?: boolean;
  bankroll_alerts?: boolean;
  calibration_alerts?: boolean;
  product_updates?: boolean;
  email_enabled?: boolean;
  in_app_enabled?: boolean;
  updated_at?: string;
}

export const notificationsPrefsApi = {
  get: () => api.get<NotificationPreferences>('/notifications/preferences').then((r) => r.data),
  update: (prefs: Partial<NotificationPreferences>) =>
    api.patch<NotificationPreferences>('/notifications/preferences', prefs).then((r) => r.data),
};

// ── Health ─────────────────────────────────────

export const healthApi = {
  check: () => api.get<{ status: string; timestamp: string }>('/health').then((r) => r.data),
};

// ── Admin / Org Audit Log ───────────────────────

export interface OrgAuditEntry {
  id: string;
  actorId: string;
  actorEmail: string | null;
  actorName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  before: unknown | null;
  after: unknown | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export const adminApi = {
  getAuditLog: (params?: { limit?: number; offset?: number; action?: string }) =>
    api.get<OrgAuditEntry[]>('/admin/audit', { params }).then((r) => r.data),
};

// ── Access Requests ────────────────────────────

export type AccessRequestStatus = 'pending' | 'approved' | 'rejected';

export interface AccessRequest {
  id: string;
  name: string;
  email: string;
  primary_sport_interest: string;
  user_profile: string;
  intent: string;
  use_case: string;
  status: AccessRequestStatus;
  rollout_phase: string;
  onboarding_wave: string;
  created_at: string;
}

export const accessRequestsApi = {
  list: (status?: AccessRequestStatus) =>
    api.get<AccessRequest[]>('/access-requests', { params: status ? { status } : undefined }).then((r) => r.data),
  updateStatus: (id: string, status: AccessRequestStatus) =>
    api.patch<AccessRequest>(`/access-requests/${id}/status`, { status }).then((r) => r.data),
};

// ── Auth / Onboarding ─────────────────────────

export interface OnboardResult {
  organizationId: string;
  organizationName: string | null;
  role: string;
  isSystemAdmin?: boolean;
}

// ── System Admin ───────────────────────────────

export interface SystemOrg {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  memberCount: number;
}

export interface SystemOrgDetail extends SystemOrg {
  pendingInviteCount: number;
}

export const systemApi = {
  listOrganizations: () =>
    api.get<SystemOrg[]>('/system/organizations').then((r) => r.data),
  getOrganization: (orgId: string) =>
    api.get<SystemOrgDetail>(`/system/organizations/${orgId}`).then((r) => r.data),
  updateOrganization: (orgId: string, data: { name: string }) =>
    api.patch<SystemOrgDetail>(`/system/organizations/${orgId}`, data).then((r) => r.data),
  listOrgMembers: (orgId: string) =>
    api.get<OrgMember[]>(`/system/organizations/${orgId}/members`).then((r) => r.data),
  updateOrgMemberRole: (orgId: string, userId: string, role: OrgRole) =>
    api.patch<{ userId: string; organizationId: string; role: OrgRole }>(
      `/system/organizations/${orgId}/members/${userId}/role`,
      { role },
    ).then((r) => r.data),
  removeOrgMember: (orgId: string, userId: string) =>
    api.delete(`/system/organizations/${orgId}/members/${userId}`).then((r) => r.data),
  sendOrgInvitation: (orgId: string, email: string, role: OrgRole) =>
    api.post<{ id: string; email: string; role: OrgRole; status: string }>(
      `/system/organizations/${orgId}/invitations`,
      { email, role },
    ).then((r) => r.data),
  listOrgJoinRequests: (orgId: string, status = 'pending') =>
    api.get<OrgJoinRequest[]>(`/system/organizations/${orgId}/join-requests`, { params: { status } }).then((r) => r.data),
  acceptOrgJoinRequest: (orgId: string, requestId: string) =>
    api.post<{ id: string; status: string }>(`/system/organizations/${orgId}/join-requests/${requestId}/accept`, {}).then((r) => r.data),
  denyOrgJoinRequest: (orgId: string, requestId: string) =>
    api.post<{ id: string; status: string }>(`/system/organizations/${orgId}/join-requests/${requestId}/deny`, {}).then((r) => r.data),
  getOrgAuditLog: (orgId: string, params?: { limit?: number; offset?: number; action?: string }) =>
    api.get<OrgAuditEntry[]>(`/system/organizations/${orgId}/audit`, { params }).then((r) => r.data),
};

export const authApi = {
  /**
   * Idempotent onboarding — safe to call on every sign-in.
   *
   * - Without `orgName`: returns existing org context (or 403 if user has no org).
   * - With `orgName`: creates a new org if the user has none, otherwise returns
   *   the existing org. Safe to call multiple times.
   */
  onboard: (orgName?: string) =>
    api.post<OnboardResult>('/auth/onboard', { orgName }).then((r) => r.data),
};

// ── Navyrx Pulse ───────────────────────────────

import type { PulseOverview, SportRecommendation } from '../types';

export interface PulseFeedFilters {
  leagueId?: string;
  subProductId?: string;
  categoryId?: string;
  timing?: string;
  status?: string;
  limit?: number;
}

export const pulseApi = {
  getOverview: () =>
    api.get<PulseOverview>('/pulse/overview').then((r) => r.data),
  getPicks: (params?: { date?: string; leagueId?: string; subProductId?: string; limit?: number }) =>
    api.get<SportRecommendation[]>('/pulse/picks', { params }).then((r) => r.data),
  getFeed: (params?: PulseFeedFilters) =>
    api.get<SportRecommendation[]>('/pulse/feed', { params }).then((r) => r.data),
  getAlerts: (params?: { leagueId?: string; subProductId?: string; unread?: boolean; limit?: number }) =>
    api.get<Alert[]>('/pulse/alerts', { params }).then((r) => r.data),
};

// ── Platform telemetry ─────────────────────────

export const getPlatformTelemetry = () =>
  api.get<PlatformTelemetry>('/platform/telemetry').then((r) => r.data);

// ── Intelligence ────────────────────────────────

export interface IntelligenceDriftResponse {
  drift: { severity: 'none' | 'mild' | 'moderate' | 'severe'; driftScore: number };
  healing: { needed: boolean; actions: string[]; requiresHumanApproval: boolean };
  memoryStats: { outcomeRecords: number; regimeRecords: number; steamPatterns: number; disagreementRecords: number };
  currentRegime: string | null;
}

export interface AutonomousAction {
  id: string;
  action_type: string;
  drift_severity: string;
  actions_taken: string[];
  requires_human: boolean;
  human_approved: boolean | null;
  created_at: string;
}

export const intelligenceApi = {
  getDrift: () =>
    api.get<IntelligenceDriftResponse>('/intelligence/drift').then((r) => r.data),
  getActions: () =>
    api.get<AutonomousAction[]>('/intelligence/actions').then((r) => r.data),
};

export default api;
